'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UnitGroup {
  category: string;
  units: string[];
}

interface UnitContextValue {
  unitGroups: UnitGroup[];
  allUnits: string[];
  loading: boolean;
  addUnit: (groupCategory: string, unit: string) => Promise<void>;
  renameUnit: (groupCategory: string, oldUnit: string, newUnit: string) => Promise<void>;
  deleteUnit: (groupCategory: string, unit: string) => Promise<void>;
  addGroup: (category: string) => Promise<void>;
  renameGroup: (oldCategory: string, newCategory: string) => Promise<void>;
  deleteGroup: (category: string) => Promise<void>;
}

const UnitContext = createContext<UnitContextValue | null>(null);

const DEFAULT_UNIT_GROUPS: UnitGroup[] = [
  { category: 'Weight', units: ['g', 'kg', 'oz', 'lb'] },
  { category: 'Volume', units: ['ml', 'L'] },
  { category: 'Count / Amount', units: ['pcs', 'count', 'dozen', 'pack'] },
];

export function UnitProvider({ children }: { children: React.ReactNode }) {
  const { company } = useAuth();
  const supabase = createClient();
  const [unitGroups, setUnitGroups] = useState<UnitGroup[]>(DEFAULT_UNIT_GROUPS);
  const [loading, setLoading] = useState(true);

  const loadUnitGroups = useCallback(async () => {
    if (!company?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('unit_groups')
        .select('category, units')
        .eq('company_id', company.id)
        .order('category');

      if (error) throw error;

      if (data && data.length > 0) {
        setUnitGroups(data.map((r) => ({ category: r.category, units: r.units as string[] })));
      } else {
        // Seed defaults for new company
        await seedDefaults(company.id);
      }
    } catch {
      // Fall back to defaults on error
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  const seedDefaults = async (companyId: string) => {
    const rows = DEFAULT_UNIT_GROUPS.map((g) => ({
      company_id: companyId,
      category: g.category,
      units: g.units,
    }));
    await supabase.from('unit_groups').insert(rows);
    setUnitGroups(DEFAULT_UNIT_GROUPS);
  };

  useEffect(() => {
    loadUnitGroups();
  }, [loadUnitGroups]);

  /** Persist updated groups array to Supabase for a specific category */
  const persistGroup = useCallback(async (companyId: string, category: string, units: string[]) => {
    await supabase
      .from('unit_groups')
      .update({ units })
      .eq('company_id', companyId)
      .eq('category', category);
  }, [supabase]);

  const addUnit = useCallback(async (groupCategory: string, unit: string) => {
    if (!company?.id) return;
    const group = unitGroups.find((g) => g.category === groupCategory);
    if (!group || group.units.includes(unit)) return;
    const newUnits = [...group.units, unit];
    await persistGroup(company.id, groupCategory, newUnits);
    setUnitGroups((prev) =>
      prev.map((g) => g.category === groupCategory ? { ...g, units: newUnits } : g)
    );
  }, [company?.id, unitGroups, persistGroup]);

  const renameUnit = useCallback(async (groupCategory: string, oldUnit: string, newUnit: string) => {
    if (!company?.id) return;
    const group = unitGroups.find((g) => g.category === groupCategory);
    if (!group) return;
    const newUnits = group.units.map((u) => (u === oldUnit ? newUnit : u));
    await persistGroup(company.id, groupCategory, newUnits);
    setUnitGroups((prev) =>
      prev.map((g) => g.category === groupCategory ? { ...g, units: newUnits } : g)
    );
  }, [company?.id, unitGroups, persistGroup]);

  const deleteUnit = useCallback(async (groupCategory: string, unit: string) => {
    if (!company?.id) return;
    const group = unitGroups.find((g) => g.category === groupCategory);
    if (!group) return;
    const newUnits = group.units.filter((u) => u !== unit);
    await persistGroup(company.id, groupCategory, newUnits);
    setUnitGroups((prev) =>
      prev.map((g) => g.category === groupCategory ? { ...g, units: newUnits } : g)
    );
  }, [company?.id, unitGroups, persistGroup]);

  const addGroup = useCallback(async (category: string) => {
    if (!company?.id) return;
    if (unitGroups.some((g) => g.category === category)) return;
    const { error } = await supabase
      .from('unit_groups')
      .insert({ company_id: company.id, category, units: [] });
    if (!error) setUnitGroups((prev) => [...prev, { category, units: [] }]);
  }, [company?.id, unitGroups, supabase]);

  const renameGroup = useCallback(async (oldCategory: string, newCategory: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('unit_groups')
      .update({ category: newCategory })
      .eq('company_id', company.id)
      .eq('category', oldCategory);
    if (!error) setUnitGroups((prev) =>
      prev.map((g) => g.category === oldCategory ? { ...g, category: newCategory } : g)
    );
  }, [company?.id, supabase]);

  const deleteGroup = useCallback(async (category: string) => {
    if (!company?.id) return;
    const { error } = await supabase
      .from('unit_groups')
      .delete()
      .eq('company_id', company.id)
      .eq('category', category);
    if (!error) setUnitGroups((prev) => prev.filter((g) => g.category !== category));
  }, [company?.id, supabase]);

  const allUnits = unitGroups.flatMap((g) => g.units);

  return (
    <UnitContext.Provider value={{ unitGroups, allUnits, loading, addUnit, renameUnit, deleteUnit, addGroup, renameGroup, deleteGroup }}>
      {children}
    </UnitContext.Provider>
  );
}

export function useUnits(): UnitContextValue {
  const ctx = useContext(UnitContext);
  if (!ctx) throw new Error('useUnits must be used within UnitProvider');
  return ctx;
}
